import ContactModel from "../models/ContactModel";
import request from "../shared/http";
import Contact from "../models/Contacts";
import Tags from "../models/Tags";
import appConfig from "../shared/js/app-config";
import Groups from "../models/Groups";
import { getAuthToken } from "../shared/authToken";

class ContactApiService {
  constructor() {
    this.endpoint = `/contacts/${appConfig.getServiceId()}`;
  }
  parseContact(item) {
    return {
      id: item.id,
      name: item.name.trim(),
      phoneNumber: item.phone_number.trim(),
      email: item.email?.trim() || "",
      company: item.company?.trim() || "",
      position: item.position?.trim() || "",
      tags: item.tags
        ?.filter((tag) => tag?.id && tag?.name)
        ?.map((tag) => (
          new Tags()
            .setId(tag.id)
            .setName(tag.name)
            .setColor(tag.color)
        )) || [],
      notes: item.notes || [],
      address: item.address?.trim() || "",
      website: item.website?.trim() || "",
      birthday: item.birthday || null,
      source: item.source || "manual",
      status: item.status || "active",
      createdAt: item.created_at,
      totalMessages: item.total_messages,
      totalRespond: item.total_respond,
    }
  }

  parseGroup(item) {
    return {
      id: item.id,
      name: item.name.trim(),
      contactsCount: item.contacts_count,
      contactsList: item.contacts_list.map((c) =>  new Contact()
        .setId(c.id)
        .setPhoneNumber(c.phone_number)
        .setName(c.name)),
      description: item.description,
      notes: item.notes || [],
      createdAt: item.created_at,
      totalMessages: item.total_messages,
    }
  }

  serializeGroup(model) {
    return {
      id: model.getId() || null,
      name: model.getName().trim(),
      description: model.getDescription(),
      contacts_list: model.getContactsList(),
      notes: model.getNotes(),
    }
  }

  serializeContact(model) {
    return {
      id: model.getId() || null,
      name: model.getName().trim(),
      phone_number: model.getPhoneNumber().trim(),
      email: model.getEmail(),
      company: model.getCompany(),
      position: model.getPosition(),
      tags: model.getTags(),
      notes: model.getNotes(),
      address: model.getAddress(),
      website: model.getWebsite(),
      birthday: model.getBirthday(),
      source: model.getSource(),
      status: model.getStatus(),
    }
  }
  async createContact(contactData) {
    try {
      if (!contactData.getName() || !contactData.getPhoneNumber()) {
        throw new Error("Name and phone are required fields");
      }
      const url = `/contacts/${appConfig.getServiceId()}`;
      const { data } = await request.post(url, this.serializeContact(contactData));
      return data;
    } catch (error) {
      console.error("Create contact error:", error);
      return {
        success: false,
        error: error.message || "Failed to create contact",
        details: "Network or validation error",
      };
    }
  }

  /**
   * Server bulk import (max 1000 contacts per request). Send smaller chunks from the client if needed.
   * Returns transformed API payload: { results, imported_count, failed_count } plus success flag.
   */
  async bulkImportContacts(contactsPayload) {
    try {
      const url = `/contacts/${appConfig.getServiceId()}/bulk-import`;
      const { data } = await request.post(url, { contacts: contactsPayload });
      return {
        success: true,
        results: data.results || [],
        imported_count: data.imported_count ?? 0,
        created_count: data.created_count ?? 0,
        updated_count: data.updated_count ?? 0,
        failed_count: data.failed_count ?? 0,
      };
    } catch (error) {
      console.error("Bulk import error:", error);
      return {
        success: false,
        error: error.message || "Bulk import failed",
        results: [],
        imported_count: 0,
        created_count: 0,
        updated_count: 0,
        failed_count: contactsPayload?.length || 0,
      };
    }
  }
   async updateContact(contactData) {
    try {
      if (!contactData.getName() || !contactData.getPhoneNumber()) {
        throw new Error("Name and phone are required fields");
      }
      const url = `/contacts/${appConfig.getServiceId()}`;
      const { data } = await request.put(url, this.serializeContact(contactData));
      return data;
    } catch (error) {
      console.error("Update contact error:", error);
      return {
        success: false,
        error: error.message || "Failed to update contact",
        details: "Network or validation error",
      };
    }
  }

  async deleteContact(id) {
    const url = `/contact/${id}`;
    return request.delete(url);
  }

  async getContactsDetails(params = {}) { 
    try {
      const url = `/contacts/${appConfig.getServiceId()}`;
      const { data } = await request.get(url, {
        params: {
        page: params.page || 1,
        limit: params.limit || 25,
        search: params.search || "",
        tags: params.tags || "",
        status: params.status || "",
        sortBy: params.sortBy || "created_at",
        sortOrder: params.sortOrder || "desc",
        tag: params.tag || "All",
      }});
      // return data.map((res) => new Contact(this.parseContact(res)));  
      const parsedContacts = (data.contacts || []).map(
      (item) => new Contact(this.parseContact(item))
    );
    const totalPages = data.totalPages || Math.ceil(data.total / data.limit);
    return {
      success: true,
      data: {
        contacts: parsedContacts,
        page: data.page,
        limit: data.limit,
        total: data.total,
        totalPages: totalPages,
      }
    };
    } catch (error) {
      console.error("Get contacts error:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch contacts",
        details: "Network error",
      };
    }
  }

  async createNotes(note, contactId) {
    const url = `/notes/${appConfig.getAccountId()}`;
    const { data } = await request.post(url, { notes: note, contact_id: contactId });
    return data;
  }

  async updateNotes(id, note) {
    const url = `/notes/${id}`;
    const { data } = await request.put(url, { notes: note });
    return data;
  }

  async deleteNote(id) {
    const url = `/notes/${id}`;
    const { data } = await request.delete(url);
    return data;
  }

  // Get a single contact by ID
  async getContactById(id) {
    try {
      const response = await request.get(`${this.endpoint}/${id}`);

      if (response.success) {
        const contact = new ContactModel({
          id: response.data.id,
          name: response.data.name,
          phone: response.data.phone,
          email: response.data.email,
          company: response.data.company,
          position: response.data.position,
          tags: response.data.tags || [],
          notes: response.data.notes,
          address: response.data.address,
          website: response.data.website,
          birthday: response.data.birthday,
          source: response.data.source,
          status: response.data.status,
          createdAt: response.data.created_at,
          updatedAt: response.data.updated_at,
        });

        return {
          success: true,
          data: contact,
        };
      } else {
        return {
          success: false,
          error: response.error || "Failed to fetch contact",
          details: response.data?.message || "Contact not found",
        };
      }
    } catch (error) {
      console.error("Get contact error:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch contact",
        details: "Network error",
      };
    }
  }

  // Bulk delete contacts
//   async deleteContacts(ids) {
//   try {
//     const response = await request.post(
//       `${this.endpoint}/bulk-delete`,
//       { ids }
//     );

//     const deletedCount = response.data?.data?.deleted_count || 0;

//     return {
//       success: true,
//       message: `${deletedCount} contacts deleted successfully`,
//       deletedCount,
//     };
//   } catch (error) {
//     console.error("Bulk delete contacts error:", error);

//     return {
//       success: false,
//       error:
//         error.response?.data?.message ||
//         error.message ||
//         "Failed to delete contacts",
//     };
//   }
// }
  
  async deleteContacts(ids) {
  try {
    const serviceId = appConfig.getServiceId();
    console.log("DELETE SERVICE ID:", serviceId);
    console.log("DELETE IDS:", ids);

    if (!serviceId) {
      throw new Error("Service ID not available yet");
    }

    const { data } = await request.post(
      `/contacts/${serviceId}/bulk-delete`,
      { ids }
    );

    return {
      success: true,
      message: `${data.deleted_count} contacts deleted successfully`,
      deletedCount: data.deleted_count,
    };

  } catch (error) {
    console.error("Bulk delete contacts FULL error:", error);
    

    return {
      success: false,
      error: error.message || "Failed to delete contacts",
      details: error.response?.data || "Network error",
    };
  }
}


  // Import contacts from file
  async importContacts(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("options", JSON.stringify(options));

      const response = await fetch(`${this.baseURL}${this.endpoint}/import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        data: {
          imported: data.imported_count,
          failed: data.failed_count,
          errors: data.errors || [],
        },
        message: `Successfully imported ${data.imported_count} contacts`,
      };
    } catch (error) {
      console.error("Import contacts error:", error);
      return {
        success: false,
        error: error.message || "Failed to import contacts",
        details: "Network or file processing error",
      };
    }
  }

  // Export contacts
  async exportContacts(format = "csv", filters = {}) {
    try {
      const response = await request.post(`${this.endpoint}/export`, {
        format,
        filters,
      });

      if (response.success) {
        return {
          success: true,
          data: {
            downloadUrl: response.data.download_url,
            expiresAt: response.data.expires_at,
          },
          message: "Export completed successfully",
        };
      } else {
        return {
          success: false,
          error: response.error || "Failed to export contacts",
          details: response.data?.message || "Unknown error occurred",
        };
      }
    } catch (error) {
      console.error("Export contacts error:", error);
      return {
        success: false,
        error: error.message || "Failed to export contacts",
        details: "Network error",
      };
    }
  }

  async getGroupList(params={}) {
    try {
      const url = `/groups/${appConfig.getServiceId()}`;
      const { data } = await request.get(url, {
        params: {
           offset: params.offset || 0,
        limit: params.limit || 20,
        search: params.search || "",
        sortBy: params.sortBy || "created_at",
        sortOrder: params.sortOrder || "desc",
        }
      });
      return data.map((res) => new Groups(this.parseGroup(res)));  
    } catch (error) {
      console.error("Get groups error:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch groups",
        details: "Network error",
      };
    }
  }
  

  async createGroup(groupData) {
    try {
      if (!groupData.getName()) {
        throw new Error("Name is required fields");
      }
      const url = `/groups/${appConfig.getServiceId()}`;
      const { data } = await request.post(url, this.serializeGroup(groupData));
      return data;
    } catch (error) {
      console.error("Create group error:", error);
      return {
        success: false,
        error: error.message || "Failed to group contact",
        details: "Network or validation error",
      };
    }
  }

  async updateGroup(groupData) {
    try {
      if (!groupData.getName()) {
        throw new Error("Name and phone are required fields");
      }
      const url = `/groups/${appConfig.getServiceId()}`;
      const { data } = await request.put(url, this.serializeGroup(groupData));
      return data;
    } catch (error) {
      console.error("Update contact error:", error);
      return {
        success: false,
        error: error.message || "Failed to update contact",
        details: "Network or validation error",
      };
    }
  }

  async deleteGroup(groupId) {
    const url = `/groups/${appConfig.getServiceId()}/${groupId}`;
    const { data } = await request.delete(url);
    return data;
  }

  async assignTagToContact(contactId, tags) {
    const url = `/tag/assign/${appConfig.getServiceId()}`;
    const { data } = await request.put(url, {
      contactId,
      tags
    });
    return data;
  }
}

export default new ContactApiService();
